// useNavigation — the v3 `layoutStore.buildNode`/`getItemField` recursive prune + active-marking
// pass (`layoutStore.ts:363–435`), ported faithfully EXCEPT the sanctioned sharpens:
//   • the `ALL_CONFIGS`/`setReportLayout` tab-hanging (`:411–422`) is DROPPED — schedule-tab
//     config is reports' concern (module 08), not nav's;
//   • `layout`/`key_id` resolution (the `addLayout` path, `:393–401`) is dropped with it — the
//     search payload rides `NavItem.meta` instead (ratified), and the report's stable machine
//     `key` rides each built node as `reportKey` for module 08's key→route mapping;
//   • report-backed LABELS become i18n keys derived from the bootstrap `reports` section `key`
//     (ratified: `key`/`name_key` label keys; BE-05-02) — never the server's raw English `name`.
//
// The nav CONTENT is not defined here: features contribute typed `NavItem[]` through
// `navContributions.ts` (the v3 484-line inline array dies with the layouts, ORP-18608/-18609).
import { computed, toValue, type ComputedRef, type MaybeRefOrGetter } from 'vue'

import { buildFreePlanNavItems } from './freePlanNavigation'
import { resolveNavItems } from './navContributions'
import type { BuiltNavNode, NavContext, NavItem, NavRouteLike } from './types'
import {
  createDefaultNavVisibilitySources,
  createNavVisibility,
  type NavVisibility,
} from './useNavVisibility'

import {
  freePlanLockedRoute,
  getAccountDetailPackageId,
  isFreePlanPackage,
  useAccount,
  useRbac,
} from '@/features/account'
import { useReportAccess } from '@/features/reports'

/**
 * The label key a report-backed nav item resolves to (BE-05-02's `name_key` shape, derived from
 * the report's stable machine `key` until the wire carries `name_key` itself). Components
 * translate it later — the model only ever carries KEYS (contract §13). The wire key is
 * snake_case; bundles enforce camelCase key style (i18n lint), so the derivation camelizes
 * (`budget_allocation` → `reports.budgetAllocation.title`).
 */
export function reportNavLabelKey(reportKey: string): string {
  const camelKey = reportKey.replace(/_(\w)/g, (_, ch: string) => ch.toUpperCase())
  return `reports.${camelKey}.title`
}

/** What `buildNavNode` needs beyond the item + route (injected; tests pass fakes). */
export interface NavBuildContext {
  visibility: NavVisibility
  /** The backing report's stable machine key (bootstrap `reports` section). */
  reportKeyOf: (reportId: number) => string | undefined
}

/**
 * v3 `getItemField`'s report indirection (`:379–381`, `:427–435`), typed: a `report` rule — or a
 * childless `multiple` whose FIRST nested rule is a `report` — resolves fields from its first
 * report id.
 */
function backingReportId(item: NavItem, hasChildren: boolean): number | undefined {
  const rule = item.visibility
  if (!rule) return undefined
  if (rule.control === 'report') return firstNumericId(rule.id)
  if (rule.control === 'multiple' && !hasChildren && Array.isArray(rule.id)) {
    const nested: unknown = rule.id[0]
    if (
      typeof nested === 'object' &&
      nested !== null &&
      (nested as { control?: unknown }).control === 'report'
    ) {
      return firstNumericId((nested as { id?: unknown }).id)
    }
  }
  return undefined
}

function firstNumericId(id: unknown): number | undefined {
  if (!Array.isArray(id)) return undefined
  const first: unknown = id[0]
  return typeof first === 'number' ? first : undefined
}

/** v3 `:388–391` — a custom target is current when the route NAME and ALL params match. */
function matchesTarget(
  item: NavItem,
  routeName: string,
  routeParams: Record<string, unknown>,
): boolean {
  if (!item.to || routeName !== item.to.name) return false
  return Object.entries(item.to.params ?? {}).every(
    ([key, value]) => String(routeParams[key] ?? '') === String(value ?? ''),
  )
}

/**
 * One node of the evaluated tree (v3 `buildNode`): recurse-and-prune children, resolve the
 * report-backed label key, mark `current`. Returns `null` when the item's visibility fails —
 * denied branches vanish with no empty disclosure left behind (v3 `filter(Boolean)`, `:373`).
 */
export function buildNavNode(
  item: NavItem,
  route: NavRouteLike,
  ctx: NavBuildContext,
): BuiltNavNode | null {
  // Prune first: an invisible node never evaluates its children (v3 pruned via the parent's
  // `filter(Boolean)`; checking up front is the same tree for less work).
  if (!ctx.visibility.isItemVisible(item)) return null

  const hasChildren = Array.isArray(item.children) && item.children.length > 0
  const routeName = String(route.name ?? '')
  const routeParams = route.params ?? {}

  const children = hasChildren
    ? (item.children ?? [])
        .map((child) => buildNavNode(child, route, ctx))
        .filter((child): child is BuiltNavNode => child !== null)
    : undefined

  // A declared group whose every child is denied must disappear as a whole. Besides avoiding an
  // empty disclosure, this keeps shell-owned groups permission-neutral: their leaves own gates.
  if (hasChildren && children?.length === 0) return null

  const childHasCurrent = children?.some((child) => child.current) ?? false

  const reportId = backingReportId(item, hasChildren)
  const reportKey = reportId === undefined ? undefined : ctx.reportKeyOf(reportId)
  // v3 `name = item.name ?? getItemField(base, 'name')` — the item's own label wins; the
  // report-derived key is the fallback, and it is a KEY, never the raw server `name`.
  const labelKey =
    item.labelKey ?? (reportKey === undefined ? undefined : reportNavLabelKey(reportKey))

  // v3 `:403–407`: a parent is current iff any child is; `matchRoutes` overrides for leaves;
  // otherwise the target (name + all params) decides. `navKey` vs route `meta.navKey` is the
  // v4-native equivalence the contract adds (§5) — additive, nothing in v3 carried `navKey`.
  const current = hasChildren
    ? childHasCurrent
    : Array.isArray(item.matchRoutes) && item.matchRoutes.length > 0
      ? item.matchRoutes.includes(routeName)
      : matchesTarget(item, routeName, routeParams) ||
        (item.navKey !== undefined && route.meta?.navKey === item.navKey)

  // Rest-omit the RAW children so the built node only ever carries BUILT children.
  const { children: _declared, ...base } = item
  return {
    ...base,
    ...(children === undefined ? {} : { children }),
    ...(labelKey === undefined ? {} : { labelKey }),
    ...(reportKey === undefined || reportKey === null ? {} : { reportKey }),
    current,
  }
}

/** Evaluate a whole contribution list against the current route (prune + mark). */
export function buildNavTree(
  items: NavItem[],
  route: NavRouteLike,
  ctx: NavBuildContext,
): BuiltNavNode[] {
  return items
    .map((item) => buildNavNode(item, route, ctx))
    .filter((node): node is BuiltNavNode => node !== null)
}

export interface UseNavigation {
  /** Which registry context feeds the tree (admin route → admin nav; dashboard-only → 09's tree). */
  context: ComputedRef<NavContext>
  items: ComputedRef<BuiltNavNode[]>
}

/**
 * Resolve + evaluate the nav tree for the current context (contract §9). The current route is
 * INJECTED (`useRoute()` at the call site — this module imports no router, per the purity rule).
 * Reactive: re-evaluates on route change and on any underlying store change (rbac, flags,
 * workspace, nav sources).
 */
export function useNavigation(route: MaybeRefOrGetter<NavRouteLike>): UseNavigation {
  const rbac = useRbac()
  const account = useAccount()
  const reportAccess = useReportAccess()
  const ctx: NavBuildContext = {
    visibility: createNavVisibility(createDefaultNavVisibilitySources()),
    reportKeyOf: (reportId) => reportAccess.reportKey(reportId),
  }

  const context = computed<NavContext>(() => {
    // v3 `DefaultLayout.vue:140` — dashboard-only users get ONLY the dashboards tree.
    if (rbac.onlyDashboard.value) return 'dashboardsOnly'
    // Contract §3: admin is a nav-SOURCE variant of the one shell, keyed off the route meta.
    if (toValue(route).meta?.requiresSysAdmin) return 'admin'
    // ORP-26799: Free gets the fixed sidebar, derived from the `app` tree below.
    return isFreePlanPackage(getAccountDetailPackageId(account.accountDetail.value))
      ? 'freePlan'
      : 'app'
  })

  const items = computed(() => {
    const source =
      context.value === 'freePlan'
        ? buildFreePlanNavItems(
            resolveNavItems('app'),
            (routeName) => freePlanLockedRoute(routeName) !== null,
          )
        : resolveNavItems(context.value)
    return buildNavTree(source, toValue(route), ctx)
  })

  return { context, items }
}
